import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
} from '@structural-climbing/ui'
import { gradesForDiscipline, LEAD_RANK_6A, type Discipline } from '@structural-climbing/validation'
import { useGymGradeConfig } from '../../layouts/useGymGradeConfig'
import type { ProvenanceRoute } from './types'

interface Props {
  /** The route being converted; null while the modal is closed. */
  route: ProvenanceRoute | null
  /** The route's own discipline, which picks the grade scale. */
  discipline: Discipline
  /**
   * The route was taken out of the competition before it was reviewed. It may
   * be an earlier copy of a route that is still in it, so say so first.
   */
  removed?: boolean
  onClose: () => void
  onConverted: () => void
}

/**
 * Turns a competition route into a normal gym route. A competition route is
 * numbered, not graded, and a gym route cannot exist without a grade, so this
 * is where it gets one. A route that already carries a grade (set before
 * competition routes went ungraded) opens with it selected.
 */
export function ConvertRouteModal({
  route,
  discipline,
  removed = false,
  onClose,
  onConverted,
}: Props) {
  const { getAccessTokenSilently } = useAuth()
  const { t } = useTranslation()
  const gradeConfig = useGymGradeConfig()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [grade, setGrade] = useState<number | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!route) return
    // Lead spans 27 French grades, so start at 6a rather than the bottom.
    setGrade(route.grade ?? (discipline === 'lead' ? LEAD_RANK_6A : null))
    setError(null)
  }, [route, discipline])

  async function convert() {
    if (!route) return
    if (!grade) {
      setError(t('routes.gradeRequired'))
      return
    }
    setSaving(true)
    setError(null)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/climbing-routes/${route.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        // One request, so the route is never out of the competition without a grade.
        body: JSON.stringify({ grade, competition_only: false }),
      })
      if (!res.ok) {
        setError(t('competitions.errorConvert'))
        return
      }
      onConverted()
      onClose()
    } catch {
      setError(t('competitions.errorConvert'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal scrollBehavior="inside" isOpen={route !== null} onClose={onClose}>
      <ModalContent>
        <ModalHeader>{t('competitions.convertTitle')}</ModalHeader>
        <ModalBody className="gap-3">
          <p className="text-sm text-foreground/60">{t('competitions.convertHint')}</p>
          {removed && (
            <p className="rounded-lg bg-warning/10 p-3 text-sm text-warning-700">
              {t('competitions.convertRemovedWarning')}
            </p>
          )}
          <Select
            label={t('routes.grade')}
            placeholder={t('routes.gradePlaceholder')}
            selectedKeys={grade ? [String(grade)] : []}
            isInvalid={!!error}
            errorMessage={error ?? undefined}
            onSelectionChange={(keys) => {
              const val = Array.from(keys)[0]
              setGrade(val ? parseInt(String(val), 10) : null)
            }}
          >
            {gradesForDiscipline(gradeConfig, discipline).map((g) => (
              <SelectItem key={String(g.rank)} textValue={g.label}>
                {g.label}
              </SelectItem>
            ))}
          </Select>
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={onClose} isDisabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button color="success" isLoading={saving} onPress={() => void convert()}>
            {t('competitions.convertConfirm')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
